import React from 'react';
import {Box, Typography, Paper, List, ListItem, ListItemText, makeStyles} from '@material-ui/core';
import {Error as ErrorIcon, Warning as WarningIcon} from '@material-ui/icons';
import {getValidationSummary} from '../utils/validation';

const useStyles = makeStyles((theme) => ({
	errorContainer: {
		marginBottom: theme.spacing(2),
		backgroundColor: '#ffebee',
		borderLeft: '4px solid #f44336',
		padding: theme.spacing(2),
		borderRadius: 4,
	},
	errorHeader: {
		display: 'flex',
		alignItems: 'center',
		marginBottom: theme.spacing(1),
	},
	errorIcon: {
		color: '#f44336',
		marginRight: theme.spacing(1),
		fontSize: 28,
	},
	errorTitle: {
		fontWeight: 'bold',
		color: '#d32f2f',
		fontSize: '1.1rem',
	},
	errorDescription: {
		color: '#666',
		marginBottom: theme.spacing(1),
		marginLeft: 36,
	},
	errorList: {
		marginTop: theme.spacing(1),
		marginLeft: 20,
		backgroundColor: '#fff',
		borderRadius: 4,
		maxHeight: 300,
		overflowY: 'auto',
	},
	errorItem: {
		padding: '8px 16px',
		cursor: 'pointer',
		borderBottom: '1px solid #f0f0f0',
		transition: 'background-color 0.2s',
		'&:hover': {
			backgroundColor: '#ffe0e0',
		},
		'&:last-child': {
			borderBottom: 'none',
		},
	},
	errorItemIcon: {
		color: '#ff9800',
		fontSize: 18,
		marginRight: 8,
		verticalAlign: 'middle',
	},
	itemName: {
		fontWeight: 600,
		color: '#333',
	},
	fieldName: {
		color: '#666',
		fontStyle: 'italic',
	},
	errorMessage: {
		color: '#d32f2f',
		fontSize: '0.85rem',
		marginTop: 2,
	},
}));

const ValidationErrorSummary = ({validationErrors, orderItems, onErrorClick}) => {
	const classes = useStyles();

	// Get summary of all errors
	const errorSummary = getValidationSummary(validationErrors);

	// Don't render if no errors
	if (!errorSummary || errorSummary.length === 0) {
		return null;
	}

	// Handle error click - scroll to item
	const handleErrorClick = (itemId) => {
		const element = document.querySelector(`[data-item-id="${itemId}"]`);
		if (element) {
			element.scrollIntoView({behavior: 'smooth', block: 'center'});
			// Highlight the row briefly
			element.style.backgroundColor = '#fff3e0';
			setTimeout(() => {
				element.style.backgroundColor = '';
			}, 2000);
		}
		if (onErrorClick) {
			onErrorClick(itemId);
		}
	};

	// Get item name for display
	const getItemName = (itemId) => {
		const item = orderItems.find(i => i.id === itemId);
		return item ? item.item_name : `Item #${itemId}`;
	};

	// Format field name for display
	const formatFieldName = (field) => {
		const fieldNames = {
			quantity: 'Quantity',
			item_price: 'Unit Price',
			coupon_discount_amount: 'Coupon Discount',
			status: 'Status',
			cancelReason: 'Cancel Reason',
			item_id: 'Item ID',
			order_item_id: 'Order Item ID',
		};
		return fieldNames[field] || field;
	};

	const errorCount = errorSummary.length;
	const itemCount = Object.keys(validationErrors).length;

	return (
		<Paper className={classes.errorContainer} elevation={0}>
			{/* Header */}
			<Box className={classes.errorHeader}>
				<ErrorIcon className={classes.errorIcon}/>
				<Typography className={classes.errorTitle}>
					Validation Error{errorCount > 1 ? 's' : ''} Found
				</Typography>
			</Box>

			{/* Description */}
			<Typography variant="body2" className={classes.errorDescription}>
				Found <strong>{errorCount}</strong> validation error{errorCount > 1 ? 's' : ''} in{' '}
				<strong>{itemCount}</strong> item{itemCount > 1 ? 's' : ''}.
				Please fix the highlighted fields below. Click on an error to jump to that field.
			</Typography>

			{/* Error List */}
			<List dense className={classes.errorList}>
				{errorSummary.map((error, index) => (
					<ListItem
						key={index}
						className={classes.errorItem}
						button
						onClick={() => handleErrorClick(error.itemId)}
					>
						<ListItemText
							primary={
								<Typography variant="body2">
									<WarningIcon className={classes.errorItemIcon}/>
									<span className={classes.itemName}>{getItemName(error.itemId)}</span>
									{' - '}
									<span className={classes.fieldName}>{formatFieldName(error.field)}</span>
								</Typography>
							}
							secondary={
								<Typography className={classes.errorMessage}>
									{error.message}
								</Typography>
							}
						/>
					</ListItem>
				))}
			</List>
		</Paper>
	);
};

export default ValidationErrorSummary;
